import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  ShieldAlert,
  ScanLine,
  TimerOff,
  Unlock,
  AlertTriangle,
  FileWarning,
  Siren,
  Search,
  Download,
  ChevronRight,
  Menu, // Added for mobile menu icon if needed
} from "lucide-react";
import { useSelector } from "react-redux";
import { TbShieldCancel } from "react-icons/tb";

// --- Dummy Data ---

// Compliance Trend (Violations by Day)
const violationTrend = [
  { day: "Mon", ghost: 2, partial: 5, override: 1, loitering: 3 },
  { day: "Tue", ghost: 0, partial: 2, override: 0, loitering: 1 },
  { day: "Wed", ghost: 4, partial: 8, override: 2, loitering: 5 }, // High violation day
  { day: "Thu", ghost: 1, partial: 3, override: 1, loitering: 2 },
  { day: "Fri", ghost: 3, partial: 4, override: 0, loitering: 4 },
];

// Breakdown of Violation Types (Pie Chart)
const violationDistribution = [
  { name: "Ghost Trips (No RFID)", value: 12, color: "#ef4444" }, // Red
  { name: "Repeated Partial Fills", value: 25, color: "#f97316" }, // Orange
  { name: "Loitering (>20m No Fill)", value: 18, color: "#eab308" }, // Yellow
  { name: "Manual Overrides", value: 8, color: "#64748b" }, // Slate
];

// High Risk Stations List
const highRiskStations = [
  {
    id: 1,
    name: "Station C-09 (East)",
    riskScore: 92,
    status: "Critical",
    issue: "4 Ghost Trips detected today",
    lastIncident: "10 min ago",
  },
  {
    id: 2,
    name: "Station A-04 (North)",
    riskScore: 78,
    status: "High",
    issue: "Frequent Manual Overrides",
    lastIncident: "2 hrs ago",
  },
  {
    id: 3,
    name: "Station D-11 (South)",
    riskScore: 65,
    status: "Warning",
    issue: "Avg wait time > 45 mins",
    lastIncident: "Yesterday",
  },
  {
    id: 4,
    name: "Station B-02 (West)",
    riskScore: 60,
    status: "Warning",
    issue: "Partial deduction pattern",
    lastIncident: "Yesterday",
  },
];

// --- Components ---

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}
  >
    {children}
  </div>
);

const RiskBadge = ({ level }) => {
  const styles = {
    Critical: "bg-red-100 text-red-700 border border-red-200 animate-pulse",
    High: "bg-orange-100 text-orange-700 border border-orange-200",
    Warning: "bg-amber-100 text-amber-700 border border-amber-200",
  };
  return (
    <span
      className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-bold uppercase tracking-wider ${styles[level]}`}
    >
      {level} Risk
    </span>
  );
};

export default function ComplianceDashboard() {
  // Safe fallback if redux is not present in preview
  const themeMode = useSelector
    ? useSelector((state) => state.theme.mode)
    : false;
  const textColorClass = themeMode ? "text-slate-100" : "text-slate-900";
  const bgColorClass = themeMode ? "bg_light_main" : "bg-white";
   const borderThem = themeMode ? "border_them_2" : "border-primary";
   const tableHedBg = themeMode ? "table_hed_bg" : "tabel_hed_def_bg";
  return (
    <div className={`w-full ${bgColorClass} h-full`}>
      {/* --- HEADER --- */}
      <div
        className={`py-2.5 px-4 md:px-8 border-b ${borderThem}  flex flex-col md:flex-row md:justify-between md:items-center gap-4 md:gap-0 sticky top-0 z-20`}
      >
        <div
          className={`flex ps-3 md:ps-0 text-sm md:text-base ${textColorClass} items-center  gap-x-2`}
        >
          <p>
            <TbShieldCancel className="cursor-pointer font-semibold" />
          </p>
          <p className="font-semibold">Compliance & Fraud</p>
        </div>

        {/* Action Buttons: Stack on mobile, Row on Desktop */}
        <div className="flex flex-col md:flex-row gap-2 md:gap-3 w-full md:w-auto">
         
          <button className={`flex justify-center items-center gap-2 px-4 py-2 bg-slate-900 border ${borderThem} text-white rounded-lg text-sm font-medium hover:bg-slate-800 shadow-lg w-full md:w-auto transition-colors`}>
            <Download className="w-4 h-4" /> Export Report
          </button>
        </div>
      </div>

      {/* --- CONTENT SCROLL AREA --- */}
      <div className=" h-[calc(100vh-60px)] pb-10 overflow-y-auto ">
        <div className="px-3 md:px-8 mt-4 md:mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card className={`p-4 md:p-5 ${bgColorClass}  ${borderThem} border-t-4 !border-t-red-500 hover:shadow-md transition-shadow`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-xs font-bold ${textColorClass} uppercase`}>
                    Ghost Trips
                  </p>
                  <h3 className="text-2xl md:text-3xl font-bold text-red-600 mt-1">
                    12
                  </h3>
                  <p className="text-xs text-red-500 mt-1 font-medium">
                    No RFID Detected
                  </p>
                </div>
                <div className="p-2 bg-red-50 rounded-lg">
                  <Siren className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                </div>
              </div>
            </Card>

            {/* Metric 2: Partial Deductions */}
             <Card className={`p-4 md:p-5 ${bgColorClass}  ${borderThem} border-t-4 !border-t-orange-500 hover:shadow-md transition-shadow`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-xs font-bold ${textColorClass} uppercase`}>
                    Partial Fills
                  </p>
                  <h3 className={`text-2xl md:text-3xl font-bold ${textColorClass} mt-1`}>
                    25
                  </h3>
                  <p className="text-xs text-orange-600 mt-1 font-medium">
                    Possible siphoning
                  </p>
                </div>
                <div className="p-2 bg-orange-50 rounded-lg">
                  <ScanLine className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
                </div>
              </div>
            </Card>

            {/* Metric 3: Loitering */}
            <Card className={`p-4 md:p-5 ${bgColorClass}  ${borderThem} border-t-4 !border-t-amber-500 hover:shadow-md transition-shadow`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-xs font-bold ${textColorClass} uppercase`}>
                    Loitering
                  </p>
                  <h3 className={`text-2xl md:text-3xl font-bold ${textColorClass} mt-1`}>
                    18
                  </h3>
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    {">"}20 mins (No Fill)
                  </p>
                </div>
                <div className="p-2 bg-amber-50 rounded-lg">
                  <TimerOff className="w-5 h-5 md:w-6 md:h-6 text-amber-500" />
                </div>
              </div>
            </Card>

            {/* Metric 4: Manual Overrides */}
             <Card className={`p-4 md:p-5 ${bgColorClass}  ${borderThem} border-t-4 !border-t-slate-500 hover:shadow-md transition-shadow`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className={`text-xs font-bold ${textColorClass} uppercase`}>
                    Overrides
                  </p>
                  <h3 className={`text-2xl md:text-3xl font-bold ${textColorClass} mt-1`}>
                    08
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 font-medium">
                    System bypass
                  </p>
                </div>
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Unlock className="w-5 h-5 md:w-6 md:h-6 text-slate-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* 2. Main Visuals Area */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
            {/* Left: Trend Analysis (Stacked Bar) */}
            <Card className={`lg:col-span-2 p-3 ${bgColorClass}  ${borderThem}`}>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6 gap-3">
                <h3 className={`text-base md:text-lg font-bold ${textColorClass}`}>
                  Violation Trends (This Week)
                </h3>
                <div className="flex flex-wrap gap-2">
                  <span className="flex items-center text-[10px] md:text-xs text-slate-600">
                    <div className="w-2 h-2 bg-red-500 mr-1 rounded-sm"></div>{" "}
                    Ghost
                  </span>
                  <span className="flex items-center text-[10px] md:text-xs text-slate-600">
                    <div className="w-2 h-2 bg-orange-500 mr-1 rounded-sm"></div>{" "}
                    Partial
                  </span>
                  <span className="flex items-center text-[10px] md:text-xs text-slate-600">
                    <div className="w-2 h-2 bg-yellow-500 mr-1 rounded-sm"></div>{" "}
                    Loitering
                  </span>
                </div>
              </div>
              <div className="h-[250px] md:h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={violationTrend}
                    margin={{ top: 20, right: 0, left: -20, bottom: 0 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke="#f1f5f9"
                    />
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 10 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: "#64748b", fontSize: 10 }}
                    />
                    <Tooltip
                      cursor={{ fill: "#f8fafc" }}
                      contentStyle={{
                        borderRadius: "8px",
                        border: "none",
                        fontSize: "12px",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                    <Bar
                      dataKey="ghost"
                      stackId="a"
                      fill="#ef4444"
                      barSize={30}
                    />
                    <Bar
                      dataKey="partial"
                      stackId="a"
                      fill="#f97316"
                      barSize={30}
                    />
                    <Bar
                      dataKey="loitering"
                      stackId="a"
                      fill="#eab308"
                      barSize={30}
                    />
                    <Bar
                      dataKey="override"
                      stackId="a"
                      fill="#cbd5e1"
                      radius={[4, 4, 0, 0]}
                      barSize={30}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Right: Violation Distribution (Donut) */}
            <Card className={`p-3 flex flex-col items-center h-auto ${bgColorClass}  ${borderThem}`}>
              <h3 className={`text-base md:text-lg font-bold ${textColorClass} w-full text-left mb-2`}>
                Total Misuse Breakdown
              </h3>
              <div className="h-[250px] w-full relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={violationDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {violationDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      wrapperStyle={{ fontSize: "11px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center Text */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-8 text-center">
                  <span className="text-3xl font-bold text-slate-800">63</span>
                  <p className="text-[10px] text-slate-500 uppercase font-semibold">
                    Incidents
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* 3. High Risk Stations Table (Flags) */}
          <Card className={`overflow-hidden mb-6 ${bgColorClass} ${borderThem}`}>
            <div className={`px-4 py-3 md:px-6 border-b ${borderThem}  flex flex-col md:flex-row justify-between items-start md:items-center bg-red-50/30 gap-2`}>
              <div>
                <h3 className={`text-base md:text-lg font-bold ${textColorClass} flex items-center gap-2`}>
                  <FileWarning className="w-5 h-5 text-red-600" />
                  High Risk Stations
                </h3>
                <p className={`text-xs md:text-sm ${textColorClass}`}>
                  Stations with repeated compliance violations
                </p>
              </div>
              <button className="text-xs md:text-sm font-semibold text-blue-600 hover:text-blue-800">
                View All Flags
              </button>
            </div>

            {/* Mobile Responsive Table Wrapper: Enables horizontal scroll on small screens */}
            <div className="overflow-x-auto w-full">
              <table className="w-full text-left text-sm text-slate-600 min-w-[800px]">
                <thead className={`${tableHedBg} ${textColorClass} border-b  ${borderThem} uppercase font-bold text-xs`}>
                  <tr>
                    <th className="px-4 md:px-6 py-4">Station Name</th>
                    <th className="px-4 md:px-6 py-4">Risk Level</th>
                    <th className="px-4 md:px-6 py-4">Risk Score</th>
                    <th className="px-4 md:px-6 py-4">Primary Violation</th>
                    <th className="px-4 md:px-6 py-4">Last Incident</th>
                    <th className="px-4 md:px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {highRiskStations.map((station) => (
                    <tr
                      key={station.id}
                      className={`transition-colors ${borderThem}`}
                    >
                      <td className={`px-4 md:px-6 py-4 font-semibold ${textColorClass}   whitespace-nowrap`}>
                        {station.name}
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                        <RiskBadge level={station.status} />
                      </td>
                      <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                station.riskScore > 80
                                  ? "bg-red-500"
                                  : "bg-orange-500"
                              }`}
                              style={{ width: `${station.riskScore}%` }}
                            />
                          </div>
                          <span className="font-bold text-slate-700">
                            {station.riskScore}/100
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-red-600 font-medium whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {station.issue}
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-4 text-slate-500 whitespace-nowrap">
                        {station.lastIncident}
                      </td>
                      <td className="px-4 md:px-6 py-4 text-right whitespace-nowrap">
                        <button className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-600">
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
